import React, {Dispatch, Fragment, SetStateAction, useContext} from 'react'
import {Dialog, Transition} from '@headlessui/react'
import Link from "./navigation/Link";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faXmark, faChevronLeft, faChevronRight} from "@fortawesome/pro-solid-svg-icons";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import CabanaLogo from "./presentational/CabanaLogo";
import FlownativeIcon from "./presentational/FlownativeIcon";
import {useIntl} from "react-intl";
import CabanaConfigurationContext from "../configuration/CabanaConfigurationContext";

type NavigationItem = {
    label: string
    path: string
    icon?: IconDefinition
}

type NavigationProps = {
    sidebarOpen: boolean
    setSidebarOpen: Dispatch<SetStateAction<boolean>>
    sidebarCollapsed: boolean
    setSidebarCollapsed: Dispatch<SetStateAction<boolean>>
    navigationItems: Array<NavigationItem>
    secondaryNavigationItems?: Array<NavigationItem>
}

const Navigation: React.FC<NavigationProps> = ({sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed, navigationItems, secondaryNavigationItems}) => {
    const intl = useIntl();
    const configuration = useContext(CabanaConfigurationContext);
    const version = configuration.version?.tag || configuration.version?.commit || 'dev';

    return (
        <>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 flex z-40 lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-600/75"/>
                    </Transition.Child>
                    <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-gray-700">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-in-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in-out duration-300"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="absolute top-0 right-0 -mr-12 pt-2">
                                    <button
                                        type="button"
                                        className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden"
                                        onClick={() => setSidebarOpen(false)}
                                    >
                                        <span className="sr-only">{intl.formatMessage({id: 'navigation.closeSidebar'})}</span>
                                        <FontAwesomeIcon icon={faXmark} className="h-6 w-6 text-white" aria-hidden="true"/>
                                    </button>
                                </div>
                            </Transition.Child>
                            <div className="flex items-center shrink-0 px-6" title={version}>
                                <CabanaLogo className="h-10 px-4" />
                            </div>
                            <nav className="mt-5 shrink-0 h-full divide-y divide-gray-600 overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.sidebar'})}>
                                <div className="space-y-1">
                                    {navigationItems.map(item => (
                                        <div key={item.path} className="px-4 hover:bg-gray-600 text-gray-400">
                                            <Link
                                                to={item.path}
                                                className={'group flex items-center gap-3 px-4 py-2 text-base leading-6 font-medium hover:text-white border-l-4 border-transparent'}
                                                activeClassName={'text-white border-l-4 border-red-500'}
                                                onClick={() => setSidebarOpen(false)}
                                            >
                                                {item.icon && <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />}
                                                {item.label}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </nav>
                        </div>
                    </Transition.Child>
                    <div className="shrink-0 w-14" aria-hidden="true">
                        {/* Dummy element to force sidebar to shrink to fit close icon */}
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:flex lg:shrink-0">
                <div className={`flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex flex-col grow bg-gray-700 pt-8 pb-4 overflow-y-auto">
                        <div className={`flex items-center shrink-0 cursor-pointer ${sidebarCollapsed ? 'justify-center' : 'px-6 gap-3'}`} title={version} onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FlownativeIcon className={sidebarCollapsed ? 'h-12 w-12' : 'h-14 w-14 shrink-0'}/>
                            {!sidebarCollapsed && <CabanaLogo className="h-10"/>}
                        </div>
                        <nav className="mt-6 flex-1 flex flex-col divide-y divide-gray-600 overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.sidebar'})}>
                            <div className="space-y-1">
                                {navigationItems.map(item => (
                                    <div key={item.path} className={`hover:bg-gray-600 text-gray-400 ${sidebarCollapsed ? '' : 'px-4'}`}>
                                        <Link
                                            to={item.path}
                                            className={`group flex items-center py-2 text-md leading-6 font-medium hover:text-white ${sidebarCollapsed ? 'justify-center' : 'gap-3 px-4 border-l-4 border-transparent'}`}
                                            activeClassName={sidebarCollapsed ? 'text-white' : 'text-white border-l-4 border-red-500'}
                                            title={sidebarCollapsed ? item.label : undefined}
                                        >
                                            {item.icon && <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />}
                                            {!sidebarCollapsed && item.label}
                                        </Link>
                                    </div>
                                ))}
                            </div>
                            {secondaryNavigationItems && (
                                <div className="mt-6 pt-6 space-y-1">
                                    {secondaryNavigationItems.map((item) => (
                                        <div key={item.path} className={`hover:bg-gray-600 text-gray-400 ${sidebarCollapsed ? '' : 'px-4'}`}>
                                            <Link
                                                to={item.path}
                                                className={`group flex items-center py-2 text-md leading-6 font-medium hover:text-white ${sidebarCollapsed ? 'justify-center' : 'gap-3 px-4 border-l-4 border-transparent'}`}
                                                activeClassName={sidebarCollapsed ? 'text-white' : 'text-white border-l-4 border-red-500'}
                                                title={sidebarCollapsed ? item.label : undefined}
                                            >
                                                {item.icon && <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />}
                                                {!sidebarCollapsed && item.label}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </nav>
                        <div className="mt-auto">
                            <div className="pt-4">
                                <button
                                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                    className={`w-full flex items-center py-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-sm ${sidebarCollapsed ? 'justify-center' : 'justify-end px-4'}`}
                                    title={sidebarCollapsed ? intl.formatMessage({id: 'navigation.expandSidebar'}) : intl.formatMessage({id: 'navigation.collapseSidebar'})}
                                >
                                    <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Navigation;
